import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Scale, Vote } from "lucide-react";

/** Phase 11.3 — public "Election Transparency Questions" display for one candidate.
 * Voluntary. Answers shown exactly as submitted. NEVER scored, ranked or rated;
 * "No"/"Other" are never framed negatively. */

const CHOICE_CLS = {
  yes: "bg-secondary text-foreground border-slate-300",
  no: "bg-secondary text-foreground border-slate-300",
  other: "bg-secondary text-foreground border-slate-300",
};
const choiceLabel = (c) => (c === "yes" ? "Yes" : c === "no" ? "No" : "Other");

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

export const TransparencyRow = ({ item }) => (
  <div className="border-b border-slate-100 last:border-0 py-3" data-testid={`transparency-row-${item.key}`}>
    <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{item.title}</p>
    <p className="text-sm text-foreground mt-0.5">{item.question}</p>
    {item.status === "answered" && (
      <div className="mt-2">
        <span className="inline-flex items-center gap-2">
          <Badge variant="outline" className={`${CHOICE_CLS[item.choice] || ""} font-semibold`}
                 data-testid={`transparency-choice-${item.key}`}>
            {choiceLabel(item.choice)}
          </Badge>
          {item.was_updated && (
            <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200 text-[10px]">
              Updated {fmtDate(item.updated_at)}
            </Badge>
          )}
        </span>
        {item.explanation && (
          <p className="text-sm text-foreground/90 mt-1.5 whitespace-pre-wrap" data-testid={`transparency-explanation-${item.key}`}>
            {item.explanation}
          </p>
        )}
      </div>
    )}
    {item.status === "no_response" && (
      <p className="text-xs text-muted-foreground mt-2 italic" data-testid={`transparency-no-response-${item.key}`}>
        Invited — No Response Yet
      </p>
    )}
    {item.status === "not_invited" && (
      <p className="text-xs text-muted-foreground mt-2 italic">Not Yet Invited</p>
    )}
    {item.status === "withheld" && (
      <p className="text-xs text-amber-800 mt-2">{item.note}</p>
    )}
  </div>
);

export const TransparencySection = ({ slug }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!slug) return;
    api.get(`/public/qa/transparency/${slug}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [slug]);
  if (!data) return null;
  const anyAnswered = data.items.some((i) => i.status === "answered");
  if (!data.invited && !anyAnswered) return null; // don't imply refusal if never invited
  return (
    <Card data-testid="transparency-section">
      <CardContent className="p-5 sm:p-6">
        <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2">
          <Vote className="h-5 w-5 text-accent" /> Election Transparency Questions
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          A voluntary, standard set of questions offered identically to every participating candidate.
        </p>
        <div className="mt-3">
          {data.items.map((item) => <TransparencyRow key={item.key} item={item} />)}
        </div>
        <p className="text-[11px] text-muted-foreground italic mt-3 inline-flex items-start gap-1.5"
           data-testid="transparency-disclaimer">
          <Scale className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {data.disclaimer}
        </p>
      </CardContent>
    </Card>
  );
};
